import { useEffect } from 'react';
import { applyPageMeta } from '@/lib/pageMeta';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const GUIDELINES_TITLE =
  'PNGGO Guidelines – Register, Login & PGK Deposit | Papua New Guinea';
const GUIDELINES_DESCRIPTION =
  'PNGGO player help for Papua New Guinea: register with SMS verification, log in securely, and deposit PGK — quick amounts or custom sums, transfer method (online transfer, SMS banking, CDM, ATM), plus mandatory transfer slip upload for verification.';
const GUIDELINES_KEYWORDS =
  'PNGGO register, PNGGO login, PNGGO deposit PGK, Papua New Guinea casino deposit, transfer slip verification, Kina Bank BSP deposit, PNG SMS verification';

function SectionBadge({ text }: { text: string }) {
  return (
    <span className="inline-block bg-[#B80E28]/15 text-[#B80E28] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
      {text}
    </span>
  );
}

const registerSteps = [
  {
    title: 'Open the official PNGGO sign-up page',
    desc: 'Use the Sign Up button on this site or go straight to the official PNGGO portal. Avoid unofficial links — only register where you trust the URL.',
  },
  {
    title: 'Enter your full name',
    desc: 'Type your legal name exactly as it appears on your bank account (Kina Bank or BSP). Withdrawals are matched to this name; mismatches can delay or block cash-outs.',
  },
  {
    title: 'Enter your mobile number',
    desc: 'Use the number you can receive SMS on. Papua New Guinea mobile numbers typically include the country code (e.g. starting with 675).',
  },
  {
    title: 'Create your password',
    desc: 'Choose 6–20 characters. Do not reuse passwords from other sites, and never share your password with anyone — including people claiming to be “support”.',
  },
  {
    title: 'Add a referrer code (if you have one)',
    desc: 'If a friend or promo gave you a referrer code, enter it in the field provided. If you do not have one, you can usually leave it blank unless the form requires it.',
  },
  {
    title: 'Verify with SMS',
    desc: 'Tap GET CODE (or equivalent). Enter the one-time code sent to your phone in the verification field. If nothing arrives, wait for the cooldown, check signal, then request again.',
  },
  {
    title: 'Complete registration',
    desc: 'Tap REGISTER. When the page confirms success, your account is ready. Use Login Here if you are not signed in automatically.',
  },
];

const loginSteps = [
  {
    title: 'Open the PNGGO login page',
    desc: 'From the official site, choose Login. If you opened PNGGO from your phone, you may see the same form inside the mobile layout.',
  },
  {
    title: 'Enter Mobile No / Username',
    desc: 'Use the mobile number or username you registered with. If you are unsure, check your confirmation SMS or your saved details.',
  },
  {
    title: 'Enter your password',
    desc: 'Use the same 6–20 character password you set at registration. If you forgot it, use the site’s “Forgot password” flow — never ask strangers on social media.',
  },
  {
    title: 'Tap LOGIN',
    desc: 'After a successful login you will reach your account dashboard. From there you can deposit in PGK, claim promotions, and play.',
  },
];

const depositSteps = [
  {
    title: 'Open Deposit (PGK)',
    desc: 'Sign in to PNGGO, then open the deposit / top-up section. Amounts are always shown in Papua New Guinea Kina (PGK).',
  },
  {
    title: 'Choose your amount',
    desc: 'Pick a quick amount with the preset buttons (10, 20, 30, or 50 PGK), or type any other valid amount in the amount field. The Step summary updates to match — for example it may read “Transfer PGK 1,000.00…” when you enter 1000.',
  },
  {
    title: 'Follow the Step box and copy bank details',
    desc: 'Read the numbered instructions at the top of the form. Note the payee reference you must enter at your bank. Use the copy icons next to Account name, Account number, and Reference so every digit matches what PNGGO shows.',
  },
  {
    title: 'Complete the transfer at your bank',
    desc: 'Send the exact PGK amount to the bank account shown — via Kina Bank or BSP online banking, mobile app, branch, CDM, ATM, or SMS banking as your bank allows. Enter the reference / payee details exactly as PNGGO displays, or the payment may not credit your account.',
  },
  {
    title: 'Select transfer method',
    desc: 'On the PNGGO form, choose how you paid: Online transfer, SMS banking, CDM (cash deposit machine), or ATM. Pick the option that matches what you actually used so support can trace the transaction.',
  },
  {
    title: 'Set transfer time',
    desc: 'Use the hour, minute, and AM/PM fields to record when you completed the transfer. These should align with your bank receipt or SMS where possible.',
  },
  {
    title: 'Upload transfer slip (required)',
    desc: 'Tap Choose File or Browse and attach a clear photo or screenshot of your receipt, confirmation screen, or SMS summary. Uploading a transfer slip is mandatory for verification — without it your deposit may be delayed or rejected.',
  },
  {
    title: 'Submit or cancel',
    desc: 'Check every field, then tap SUBMIT. Keep your bank proof until your PGK balance updates. Use CANCEL only if you want to leave without sending this deposit request.',
  },
];

function ReferenceShot({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption: string;
}) {
  return (
    <figure className="max-w-md mx-auto lg:mx-0 mb-10">
      <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden shadow-lg">
        <img src={src} alt={alt} className="w-full h-auto object-contain object-top" loading="lazy" />
      </div>
      <figcaption className="text-white/45 text-xs text-center mt-3 leading-5">{caption}</figcaption>
    </figure>
  );
}

export default function GuidelinesPage() {
  const base = import.meta.env.BASE_URL;
  const registerRefSrc = `${base}guidelines/pnggo-register-reference.png`;
  const loginRefSrc = `${base}guidelines/pnggo-login-reference.png`;
  const depositRefSrc = `${base}guidelines/pnggo-deposit-reference.png`;

  useEffect(() => {
    applyPageMeta({
      title: GUIDELINES_TITLE,
      description: GUIDELINES_DESCRIPTION,
      path: '/guidelines',
      keywords: GUIDELINES_KEYWORDS,
      ogDescription:
        'PNGGO tutorials: sign up & log in with SMS, then deposit PGK using quick amounts or custom sums, choose bank transfer method, and upload a transfer slip for verification.',
      twitterDescription:
        'Register, log in & deposit on PNGGO in PGK: 10/20/30/50 shortcuts, custom amounts, online/SMS/CDM/ATM method, mandatory transfer slip.',
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pnggo-page-motif">
      <Navbar />

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <SectionBadge text="Player Help" />
            <h1 className="text-white text-3xl md:text-5xl font-bold mb-5 leading-tight">
              Guidelines: Register, Login &amp; Deposit on PNGGO
            </h1>
            <p className="text-white/60 text-sm md:text-base leading-7 mb-8">
              Step-by-step help for Papua New Guinea players: create an account, sign in, and fund your wallet in PGK — including choosing deposit amounts, transfer method, and uploading your transfer slip for verification.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://pnggo.bet/RFLANDINGPAGEPNG"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#B80E28] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#9a0b21] transition-colors whitespace-nowrap"
              >
                Go to PNGGO <i className="fas fa-chevron-right text-xs"></i>
              </a>
              <a
                href="/payment"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-7 py-3 rounded-full hover:border-[#B80E28] hover:text-[#B80E28] transition-colors whitespace-nowrap"
              >
                PGK banking (Kina &amp; BSP)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Register */}
      <section className="pb-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <h2 className="text-white text-2xl md:text-3xl font-bold mb-3">How to register an account</h2>
            <p className="text-white/55 text-sm leading-7 max-w-3xl">
              Registration collects the details PNGGO needs to secure your account and pay you correctly. Take an extra minute to enter your name and phone number accurately — it saves headaches later.
            </p>
          </div>
          <ReferenceShot
            src={registerRefSrc}
            alt="PNGGO.VIP mobile registration form showing full name, mobile number, password, referrer code, SMS verification and register button"
            caption="Reference: official PNGGO.VIP sign-up screen (mobile). Field labels and layout may be updated by PNGGO from time to time."
          />
          <div className="grid grid-cols-1 gap-4 max-w-3xl">
            {registerSteps.map((s, i) => (
              <div
                key={i}
                className="flex gap-4 bg-[#1a1a1a] border border-white/5 rounded-xl p-5 md:p-6 hover:border-[#B80E28]/25 transition-colors"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#B80E28]/15 flex items-center justify-center text-[#B80E28] font-bold text-sm">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base mb-2">{s.title}</h3>
                  <p className="text-white/55 text-sm leading-6">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 max-w-3xl rounded-xl border border-[#B80E28]/25 bg-[#B80E28]/5 px-5 py-4">
            <p className="text-white/80 text-sm leading-6">
              <strong className="text-white">Important for PNG players:</strong> Your registered name must match your bank records (Kina Bank or BSP) so withdrawals can be verified. If you made a typo at sign-up, contact PNGGO support before you deposit large amounts.
            </p>
          </div>
        </div>
      </section>

      {/* Login */}
      <section className="pb-20 bg-[#111] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 pt-16">
          <div className="mb-10">
            <h2 className="text-white text-2xl md:text-3xl font-bold mb-3">How to log in</h2>
            <p className="text-white/55 text-sm leading-7 max-w-3xl">
              Logging in uses the mobile number or username and password you chose at registration. Keep your credentials private — PNGGO staff will never ask for your password or SMS code in chat.
            </p>
          </div>
          <ReferenceShot
            src={loginRefSrc}
            alt="PNGGO.VIP mobile login screen with mobile number or username, password fields and login button"
            caption="Reference: official PNGGO.VIP login screen (mobile). Use the same mobile number or username you registered with."
          />
          <div className="grid grid-cols-1 gap-4 max-w-3xl">
            {loginSteps.map((s, i) => (
              <div
                key={i}
                className="flex gap-4 bg-[#1a1a1a] border border-white/5 rounded-xl p-5 md:p-6 hover:border-[#B80E28]/25 transition-colors"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#B80E28]/15 flex items-center justify-center text-[#B80E28] font-bold text-sm">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base mb-2">{s.title}</h3>
                  <p className="text-white/55 text-sm leading-6">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deposit */}
      <section className="pb-20 border-t border-white/5 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 pt-16">
          <div className="mb-10">
            <h2 className="text-white text-2xl md:text-3xl font-bold mb-3">How to make a PGK deposit</h2>
            <p className="text-white/55 text-sm leading-7 max-w-3xl">
              After you log in, use the deposit screen to choose how much to send (quick picks or your own amount), tell PNGGO how you transferred the money, and attach your proof. The transfer slip upload is required so your deposit can be verified against your bank records.
            </p>
          </div>
          <ReferenceShot
            src={depositRefSrc}
            alt="PNGGO mobile deposit form showing PGK amount, preset buttons 10 to 50, bank details with copy icons, transfer method dropdown, transfer time, and transfer slip upload"
            caption="Reference: PNGGO deposit screen (mobile). Bank name, account details, and reference codes are assigned per session — always use what your live screen shows."
          />
          <div className="grid grid-cols-1 gap-4 max-w-3xl">
            {depositSteps.map((s, i) => (
              <div
                key={i}
                className="flex gap-4 bg-[#1a1a1a] border border-white/5 rounded-xl p-5 md:p-6 hover:border-[#B80E28]/25 transition-colors"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#B80E28]/15 flex items-center justify-center text-[#B80E28] font-bold text-sm">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base mb-2">{s.title}</h3>
                  <p className="text-white/55 text-sm leading-6">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 max-w-3xl rounded-xl border border-[#B80E28]/25 bg-[#B80E28]/5 px-5 py-4">
            <p className="text-white/80 text-sm leading-6">
              <strong className="text-white">Transfer slip is mandatory:</strong> PNGGO matches your deposit using the slip or screenshot you upload. Use a sharp image that shows amount, time, reference, and sender details where visible. If something fails, contact live chat with your reference — never send passwords or full card numbers.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
